import { type SvgIconProps, useTheme } from '@mui/material';
import { ArrowRight } from '@portals/icons/src/ArrowRight/ArrowRight';
import React from 'react';

import type { SxProps, Theme } from '../../themes/themes';
import { FinalGridContainer, FinalGridContainerProps, FinalGridItem } from '../FinalGrid/FinalGrid';
import { Typography } from '../Typography/Typography';
import type { TypographyProps } from '../Typography/Typography.types';
import { getStyles } from './ProductLink.styles';

export interface ProductLinkProps extends FinalGridContainerProps {
  children: React.ReactNode;
  description?: string;

  startIcon?: null | React.ReactElement<SvgIconProps>;
  endIcon?: null | React.ReactElement<SvgIconProps>;

  disabled?: boolean;
  className?: string;

  primaryTypographyVariant?: TypographyProps['variant'];
  primaryTypographyFontWeight?: TypographyProps['fontWeight'];
  primaryTypographyBehaviour?: 'ellipsis' | 'wrap';

  secondaryTypographyVariant?: TypographyProps['variant'];
  secondaryTypographyFontWeight?: TypographyProps['fontWeight'];

  component?: 'div' | 'button' | 'a' | 'span' | 'li';
  href?: HTMLAnchorElement['href'];
  target?: HTMLAnchorElement['target'];
  rel?: HTMLAnchorElement['rel'];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (event: any) => void;
  tabIndex?: number;
  ariaLabel?: string;
  onKeyDown?: (event: unknown) => void;
}

export const ProductLink: React.FC<ProductLinkProps> = ({
  children,
  description,
  startIcon,
  endIcon = <ArrowRight />,
  disabled,
  primaryTypographyVariant = 'button',
  primaryTypographyFontWeight = '600',
  primaryTypographyBehaviour = 'ellipsis',
  secondaryTypographyVariant = 'body2',
  secondaryTypographyFontWeight = '400',
  component = 'div',
  ...props
}: ProductLinkProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme, { disabled, startIcon, endIcon, component });
  const sx = Object.assign({}, props.sx, styles.container);

  return (
    <FinalGridContainer component={component} {...props} columnGap={0} rowGap={0} sx={sx}>
      <FinalGridItem
        md={12}
        lg={12}
        innerPadding="both"
        sx={{ minWidth: 0, maxWidth: '100%', display: 'flex', gap: { xs: 4, md: 6, lg: 6 } }}
      >
        {startIcon
          ? React.cloneElement(startIcon, {
              fontSize: 'inherit',
              sx: { ...styles.startIcon, ...startIcon.props?.sx } as SxProps,
            })
          : null}
        <Typography
          variant={primaryTypographyVariant}
          sx={primaryTypographyBehaviour === 'ellipsis' ? styles.textEllipsis : styles.textWrap}
          fontWeight={primaryTypographyFontWeight}
        >
          {children}
        </Typography>
        {endIcon
          ? React.cloneElement(endIcon, {
              fontSize: 'inherit',
              sx: { ...styles.endIcon, ...endIcon.props?.sx } as SxProps,
            })
          : null}
      </FinalGridItem>
      {description ? (
        <FinalGridItem md={12} lg={12} innerPadding="both" display={{ lg: 'block', xs: 'none', md: 'block' }}>
          <Typography
            variant={secondaryTypographyVariant}
            sx={styles.description}
            fontWeight={secondaryTypographyFontWeight}
          >
            {description}
          </Typography>
        </FinalGridItem>
      ) : null}
    </FinalGridContainer>
  );
};
